import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type { AxiosError } from "axios";
import { useRouter } from "next/router";
import { useCallback } from "react";
import useSWR from "swr";
import type { NskSwrErrorResponse, SwrReturnType } from "~/models/Api";
import type { Deal, DealIndexClient, DealSearchCondition } from "~/models/Deal";
import { convertQueryStringUrl } from "~/utils/DealSearchConditionUtils";
import { generatePath } from "~/utils/generatePath";

const paths = {
	index: "/deals",
	show: "/deals/:id"
} as const;
type TIndex = (
	query: DealSearchCondition
) => SwrReturnType<Pageable<DealIndexClient>>;
type TShow = (id: number | null) => SwrReturnType<Deal>;
type ReturnType = {
	index: TIndex;
	show: TShow;
};

const buildShowPath = (id: number): string => generatePath(paths.show, { id });

export const dealCacheKey = (id: number): string => buildShowPath(id);

export const useDeals = (): ReturnType => {
	const router = useRouter();
	const { notify } = useNotification();

	const index: TIndex = (query: DealSearchCondition) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, error } = useSWR<Pageable<DealIndexClient>>(
			convertQueryStringUrl(paths.index, query)
		);
		return {
			data: data ?? null,
			error,
			isLoading: !data && !error,
			mutate
		};
	};

	const handleDealShowError = useCallback(
		(error: AxiosError<NskSwrErrorResponse>) => {
			if (error.response?.status === 404) {
				notify({
					content: "この案件にアクセスする権限がないか、案件が存在しません。",
					variant: "error"
				});
				void router.replace(generatePath(paths.index));
			}
		},
		[notify, router]
	);

	const show: TShow = (id: number | null) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, error } = useSWR<Deal>(
			id === null ? null : paths.show.replace(":id", String(id)),
			{ onError: handleDealShowError }
		);
		return {
			data: data ?? null,
			error,
			isLoading: !data && !error,
			mutate
		};
	};
	return { index, show };
};
